import { useLayoutEffect, useRef, useMemo } from 'react'
import { useFieldArray, useWatch } from 'react-hook-form'

import RuleEntryDetails from '../RuleEntryDetails/RuleEntryDetails'
import RuleConditions from '../RuleEntryCondition/RuleEntryCondition'
import styles from './RuleEntry.module.sass'
import RuleEntryOutput from '../RuleEntryOutput/RuleEntryOutput'
import PriorityRuleEntryOutput from '../RuleEntryOutput/PriorityRuleEntryOutput'
import AutomationRuleEntryOutput from '../RuleEntryOutput/AutomationRuleEntryOutput'
import UserRuleEntryOutput from '../RuleEntryOutput/UserRuleEntryOutput'

const renderOutput = (ruleType, taskType, path, data, option, fields) => {
	switch (ruleType) {
		case 'PriorityRule':
			return (
				<PriorityRuleEntryOutput
					path={path}
					data={data}
					options={option}
				/>
			)
		case 'AutomationRule':
			return (
				<AutomationRuleEntryOutput
					path={path}
					data={data}
					options={option}
				/>
			)
		case 'UserExclusionRule':
			return (
				fields.length === 0 && (
					<UserRuleEntryOutput
						path={path}
						data={data}
						options={option}
					/>
				)
			)
		case 'EventsSubscriptionRule':
			return taskType === 'SystemNotifications' ? (
				<RuleEntryOutput
					shouldHaveOutputMessage
					path={path}
					data={data}
					options={option}
				/>
			) : (
				<UserRuleEntryOutput
					shouldHaveOutputMessage
					path={path}
					data={data}
				/>
			)
		default:
			return <RuleEntryOutput path={path} data={data} options={option} />
	}
}

const RuleEntry = ({
	path,
	onDelete,
	options,
	data,
	ruleType,
	taskType,
	onResetEntry,
}) => {
	const rulesContainerRef = useRef(null)
	const { fields, append, remove } = useFieldArray({
		name: `${path}.logicalConditions`,
		keyName: 'key',
		shouldUnregister: true,
	})

	const action = useWatch({ name: `${path}.action` })
	const actionEntity = useWatch({ name: `${path}.actionEntity` })

	const actionEntityOptions = useMemo(
		() =>
			actionEntity && action
				? {
						...(options?.entities?.[actionEntity?.toLowerCase()]?.[
							action
						] ??
							options?.entities?.[actionEntity?.toLowerCase()] ??
							{}),
				  }
				: {},
		[actionEntity, options, action]
	)

	useLayoutEffect(() => {
		const element = rulesContainerRef.current
		if (!element) return

		const computeHeight = () => {
			element?.style.setProperty(
				'--root-height',
				`${element.lastChild?.offsetTop}px`
			)
		}

		const observer = new ResizeObserver(computeHeight)
		observer.observe(element)
		computeHeight()

		return () => {
			observer.unobserve(element)
		}
	}, [fields])

	const output = useMemo(
		() => renderOutput(ruleType, taskType, path, data, options, fields),
		[data, fields, options, path, ruleType, taskType]
	)

	return (
		<div className={styles['root']}>
			<RuleEntryDetails
				path={path}
				data={data}
				options={options}
				onDelete={onDelete}
				onChange={onResetEntry}
			/>
			{fields.length ? (
				<div
					ref={rulesContainerRef}
					className={`${styles['conditions-container']} ${
						styles['odd']
					} ${fields.length > 1 ? styles['multiple'] : ''}`}
				>
					{fields.map(({ key, logicalCondition }, index) => (
						<RuleConditions
							key={key}
							path={`${path}.logicalConditions.${index}`}
							data={logicalCondition}
							options={{ ...options, ...actionEntityOptions }}
							hasOutput={!!output}
							onRemove={
								fields.length >= 1
									? () => {
											remove(index)
									  }
									: undefined
							}
							onAppend={
								index >= fields.length - 1
									? () => {
											append({ logicalCondition: [{}] })
									  }
									: undefined
							}
						/>
					))}
				</div>
			) : null}
			{output}
		</div>
	)
}

export default RuleEntry
